import { auth } from '../lib/firebase';
import { normalizeBackendUrl } from './backendUrl';

const BACKEND_URL = normalizeBackendUrl(import.meta.env.VITE_BACKEND_URL);

async function getIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Vous devez être connecté en admin.');
  return user.getIdToken();
}

async function apiFetch<T>(
  path: string,
  init: { method?: string; body?: BodyInit | null } = {}
): Promise<T> {
  const token = await getIdToken();
  const response = await fetch(`${BACKEND_URL}${path}`, {
    method: init.method || 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
    },
    body: init.body,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((payload as { error?: string })?.error || `Request failed (${response.status})`);
  }
  return payload as T;
}

export type MatchConfidence = 'high' | 'medium' | 'low' | 'none';

export type CiqualCandidateLite = {
  ciqualCode: number;
  nameRaw: string;
  score: number;
  preparationState?: string | null;
  kcal?: number | null;
};

export type UsdaCandidateLite = {
  id: string;
  fdcId: number;
  portionId: number;
  descriptionEn: string;
  modifier: string;
  unitWeight_g: number;
  score: number;
};

export type MassEnrichmentItem = {
  id: string;
  ingredientId: string;
  ingredientName: string;
  language?: string | null;
  preferredUnit?: string | null;
  needsCiqual: boolean;
  needsConversion: boolean;
  conversionKind: 'unitWeight' | 'density' | null;
  nameEn?: string | null;
  ciqualConfidence: MatchConfidence;
  usdaConfidence: MatchConfidence;
  suggestedCiqualCode?: number | null;
  suggestedPortionId?: string | null;
  suggestedUnitWeight_g?: number | null;
  ciqualCandidates: CiqualCandidateLite[];
  usdaCandidates: UsdaCandidateLite[];
  status: string;
};

export type MassEnrichmentRun = {
  id: string;
  status: string;
  language?: string;
  pageSize?: number;
  offset?: number;
  eligibleTotal?: number;
  skippedComplete?: number;
  queued?: number;
  processed?: number;
  nextOffset?: number | null;
  errors?: { id: string; error: string }[];
};

export type MassEnrichmentPreview = {
  eligibleTotal: number;
  skippedComplete: number;
  needCiqual: number;
  needConversion: number;
  needBoth: number;
  pageSize: number;
  offset: number;
  pageCount: number;
  nextOffset: number | null;
  pagePreview: {
    id: string;
    name: string;
    preferredUnit: string | null;
    needsCiqual: boolean;
    needsConversion: boolean;
    conversionKind: string | null;
  }[];
};

export function previewMassEnrichment(body: {
  language?: string;
  limit?: number;
  offset?: number;
}) {
  return apiFetch<{ success: boolean; preview: MassEnrichmentPreview }>(
    '/api/mass-enrichment/preview',
    { method: 'POST', body: JSON.stringify(body) }
  );
}

export function runMassEnrichment(body: {
  language?: string;
  limit?: number;
  offset?: number;
}) {
  return apiFetch<{ success: boolean; report: MassEnrichmentRun & { runId: string } }>(
    '/api/mass-enrichment/run',
    { method: 'POST', body: JSON.stringify(body) }
  );
}

export function getLatestMassEnrichmentRun() {
  return apiFetch<{ success: boolean; run: MassEnrichmentRun | null }>(
    '/api/mass-enrichment/runs/latest'
  );
}

export function getMassEnrichmentRun(runId: string) {
  return apiFetch<{ success: boolean; run: MassEnrichmentRun }>(
    `/api/mass-enrichment/runs/${runId}`
  );
}

export function listMassEnrichmentItems(runId: string, status = 'pending') {
  return apiFetch<{ success: boolean; items: MassEnrichmentItem[] }>(
    `/api/mass-enrichment/runs/${runId}/items?status=${encodeURIComponent(status)}`
  );
}

export function applyMassEnrichmentItem(body: {
  runId: string;
  ingredientId: string;
  ciqualCode?: number | null;
  skipCiqual?: boolean;
  portionId?: string | null;
  skipConversion?: boolean;
  unitWeight_g?: number | null;
  density_g_per_mL?: number | null;
}) {
  return apiFetch<{ success: boolean }>('/api/mass-enrichment/apply', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function skipMassEnrichmentItem(runId: string, ingredientId: string) {
  return apiFetch<{ success: boolean }>('/api/mass-enrichment/skip', {
    method: 'POST',
    body: JSON.stringify({ runId, ingredientId }),
  });
}
